import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IosShareIcon from '@mui/icons-material/IosShare';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useSnackbar } from '@/app/SnackbarProvider';
import { type InstallContext, isIosDevice } from '@/lib/platform/install';
import { siteAddress } from '@/lib/site';

interface Step {
  icon: ReactNode;
  text: ReactNode;
}

const strong = (s: string) => <strong>{s}</strong>;

/** Adding to the home screen from Safari on iPhone. */
const SAFARI_STEPS: Step[] = [
  { icon: <MoreHorizIcon />, text: <>Tap the {strong('⋯')} (three dots) menu in Safari.</> },
  { icon: <IosShareIcon />, text: <>Tap {strong('Share')} (a box with an arrow pointing up).</> },
  { icon: <MoreHorizIcon />, text: <>Tap {strong('View More')}.</> },
  { icon: <AddBoxOutlinedIcon />, text: <>Tap {strong('Add to Home Screen')}.</> },
  { icon: <CheckCircleOutlineIcon />, text: <>Tap {strong('Add')}. TradePrep is now on your home screen.</> },
];

function stepsFor(context: InstallContext): { intro?: string; steps: Step[]; note?: string } {
  switch (context) {
    case 'ios-safari':
      return {
        steps: SAFARI_STEPS,
        note: "Can't see Add to Home Screen? Make sure you're not in a private tab. If you opened this from an email and the page is inside your email app, tap the compass icon (Open in Safari) first.",
      };
    case 'ios-other':
      return {
        intro: "Tap Share in your browser's menu bar, or the ⋯ menu, then follow these steps. If you can't find it, open this page in Safari instead.",
        steps: [
          { icon: <IosShareIcon />, text: <>Tap {strong('Share')} (a square with an arrow), or the {strong('⋯')} menu.</> },
          { icon: <AddBoxOutlinedIcon />, text: <>Tap {strong('Add to Home Screen')}.</> },
          { icon: <CheckCircleOutlineIcon />, text: <>Tap {strong('Add')}.</> },
        ],
      };
    case 'in-app': {
      const ios = isIosDevice();
      return {
        intro: ios
          ? "This page is open inside another app (such as your email app), which can't add TradePrep to your home screen. Open it in Safari first."
          : "This page is open inside another app, which can't add TradePrep to your home screen. Open it in your phone's browser first.",
        steps: [
          { icon: <MoreVertIcon />, text: <>Look for the {strong('compass')} icon, or the {strong('⋯')} / {strong('⋮')} menu, in this window.</> },
          { icon: <IosShareIcon />, text: <>Choose {strong(ios ? 'Open in Safari' : 'Open in browser')} (it may say {strong('Open in Browser')}).</> },
          // Once it's open in Safari, the same steps as for anyone in Safari.
          ...(ios ? SAFARI_STEPS : []),
        ],
        note: "Safari is a separate browser, so you'll sign in to TradePrep there once. Can't find the option? Copy the link, open Safari and paste it in.",
      };
    }
    case 'android-manual':
      return {
        steps: [
          { icon: <MoreVertIcon />, text: <>Tap the {strong('⋮')} menu (top right in Chrome and Samsung Internet, bottom right in Firefox).</> },
          { icon: <AddBoxOutlinedIcon />, text: <>Tap {strong('Install app')} or {strong('Add to Home screen')}.</> },
          { icon: <CheckCircleOutlineIcon />, text: <>Tap {strong('Install')} or {strong('Add')}.</> },
        ],
        note: "Don't see it? Make sure you're using Chrome, and that TradePrep isn't already installed.",
      };
    default:
      return { steps: [] };
  }
}

/** Step-by-step help for browsers that can't install with one tap. */
export function InstallDialog({ open, context, onClose }: { open: boolean; context: InstallContext; onClose: () => void }) {
  const notify = useSnackbar();
  const { intro, steps, note } = stepsFor(context);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(siteAddress());
      notify(`Copied ${siteAddress().replace('https://', '')}. Paste it into Safari.`);
    } catch {
      notify(`Open ${siteAddress().replace('https://', '')} in your browser.`, 'info');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="install-title">
      <DialogTitle id="install-title">{context === 'in-app' ? 'Open in your browser first' : 'Add TradePrep to your home screen'}</DialogTitle>
      <DialogContent>
        {intro && <Typography sx={{ mb: 1 }}>{intro}</Typography>}
        {context !== 'in-app' && (
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            It opens full screen like an app, with no browser bars, and your progress is right where you left it.
          </Typography>
        )}
        <List dense>
          {steps.map((s, i) => (
            <ListItem key={i} alignItems="flex-start" disableGutters>
              <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>{s.icon}</ListItemIcon>
              <ListItemText primary={<>{i + 1}. {s.text}</>} slotProps={{ primary: { variant: 'body1' } }} />
            </ListItem>
          ))}
        </List>
        {note && (
          <Typography variant="body2" color="text.secondary">
            {note}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {context === 'in-app' && (
          <Button startIcon={<ContentCopyIcon />} onClick={() => void copyLink()}>
            Copy link
          </Button>
        )}
        <Button variant="contained" onClick={onClose}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
